import { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Tag, Space, Tooltip, Progress, Image, Empty, message } from '@/shared/ui/primitives';
import { UploadOutlined, DeleteOutlined, StarFilled, StarOutlined, VideoCameraOutlined } from '@/shared/ui/icons';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { roomAdApi, type AdImageDTO } from '../api/roomAd.api';
import { roomWantedApi } from '@/features/room-wanted/api/roomWanted.api';

interface Props {
  adId: number | string;
  resource?: 'room-ad' | 'room-wanted';
  /** Called after any mutation so the parent may refetch the ad */
  onChange?: () => void;
}

const ACCEPTED = 'image/jpeg,image/jpg,image/png,image/webp,image/gif';
const MAX_BYTES = 10 * 1024 * 1024;
const VIDEO_ACCEPTED = 'video/mp4,video/quicktime';
const VIDEO_MAX_BYTES = 200 * 1024 * 1024;

export default function AdImageManager({ adId, resource = 'room-ad', onChange }: Props) {
  const { t } = useTranslation();
  const [media, setMedia] = useState<AdImageDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState<number | null>(null);
  const [videoProgress, setVideoProgress] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const videoInput = useRef<HTMLInputElement | null>(null);
  const imageApi = resource === 'room-wanted' ? roomWantedApi : roomAdApi;

  const images = media.filter((m) => (m.mediaType ?? 'IMAGE') !== 'VIDEO');
  const videos = media.filter((m) => m.mediaType === 'VIDEO');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const list = await imageApi.listImages(adId);
      setMedia(list.sort((a, b) => (a.position ?? 0) - (b.position ?? 0)));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('roomAds.images.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [adId, imageApi, t]);

  useEffect(() => { reload(); }, [reload]);

  const validate = (files: File[]): boolean => {
    for (const f of files) {
      if (f.size > MAX_BYTES) {
        toast.error(t('roomAds.images.tooLarge', { name: f.name, max: '10 MB' }));
        return false;
      }
      if (!ACCEPTED.includes(f.type)) {
        toast.error(t('roomAds.images.badType', { name: f.name }));
        return false;
      }
    }
    return true;
  };

  const validateVideos = (files: File[]): boolean => {
    for (const f of files) {
      if (f.size > VIDEO_MAX_BYTES) {
        toast.error(t('roomAds.videos.tooLarge', { name: f.name, max: '200 MB' }));
        return false;
      }
      if (!VIDEO_ACCEPTED.includes(f.type)) {
        toast.error(t('roomAds.videos.badType', { name: f.name }));
        return false;
      }
    }
    return true;
  };

  const handleFiles = async (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    if (!validate(arr)) return;
    setProgress(0);
    try {
      await imageApi.uploadImages(adId, arr, (pct) => setProgress(pct));
      toast.success(t('roomAds.images.uploaded', { count: arr.length }));
      await reload();
      onChange?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('roomAds.images.uploadFailed'));
    } finally {
      setProgress(null);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const handleVideoFiles = async (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    if (!validateVideos(arr)) return;
    setVideoProgress(0);
    try {
      await imageApi.uploadVideos(adId, arr, (pct) => setVideoProgress(pct));
      toast.success(t('roomAds.videos.uploaded', { count: arr.length }));
      await reload();
      onChange?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('roomAds.videos.uploadFailed'));
    } finally {
      setVideoProgress(null);
      if (videoInput.current) videoInput.current.value = '';
    }
  };

  const handleDelete = async (img: AdImageDTO) => {
    setBusyId(img.id);
    try {
      await imageApi.deleteImage(adId, img.id);
      toast.success(t('roomAds.images.deleted'));
      await reload();
      onChange?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('roomAds.images.deleteFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const handleSetPrimary = async (img: AdImageDTO) => {
    if (img.isPrimary) return;
    setBusyId(img.id);
    try {
      await imageApi.setPrimaryImage(adId, img.id);
      toast.success(t('roomAds.images.primarySet'));
      await reload();
      onChange?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('roomAds.images.primaryFailed'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <input
          ref={fileInput}
          type="file"
          multiple
          accept={ACCEPTED}
          style={{ display: 'none' }}
          onChange={(e) => e.target.files && handleFiles(e.target.files)}
        />
        <Button
          icon={<UploadOutlined />}
          type="primary"
          disabled={progress !== null || videoProgress !== null}
          loading={progress !== null}
          onClick={() => fileInput.current?.click()}
        >
          {t('roomAds.images.upload')}
        </Button>
        <input
          ref={videoInput}
          type="file"
          multiple
          accept={VIDEO_ACCEPTED}
          style={{ display: 'none' }}
          onChange={(e) => e.target.files && handleVideoFiles(e.target.files)}
        />
        <Button
          icon={<VideoCameraOutlined />}
          disabled={progress !== null || videoProgress !== null}
          loading={videoProgress !== null}
          onClick={() => videoInput.current?.click()}
        >
          {t('roomAds.videos.upload')}
        </Button>
        <Tag>{t('roomAds.images.count', { count: images.length })}</Tag>
        <Tag>{t('roomAds.videos.count', { count: videos.length })}</Tag>
        <span style={{ color: '#8c8c8c', fontSize: 12 }}>{t('roomAds.images.hint')}</span>
      </Space>

      {progress !== null && (
        <Progress percent={progress} size="small" status={progress >= 100 ? 'success' : 'active'} style={{ maxWidth: 360, marginBottom: 12 }} />
      )}
      {videoProgress !== null && (
        <Progress percent={videoProgress} size="small" status={videoProgress >= 100 ? 'success' : 'active'} style={{ maxWidth: 360, marginBottom: 12 }} />
      )}

      {loading ? null : images.length === 0 ? (
        <Empty description={t('roomAds.images.empty')} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
          {images.map((img) => (
            <div key={img.id} style={{
              border: img.isPrimary ? '2px solid #1677ff' : '1px solid #f0f0f0',
              borderRadius: 8, padding: 8, background: '#fafafa', position: 'relative',
            }}>
              <Image
                src={img.imageUrl}
                alt={`#${img.id}`}
                style={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 4 }}
                preview={false}
                fallback="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxODAiIGhlaWdodD0iMTQwIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZWVlIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIGZpbGw9IiM5OTkiPkltYWdlIG5vdCBhdmFpbGFibGU8L3RleHQ+PC9zdmc+"
              />
              <div style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Tag color={img.isPrimary ? 'blue' : 'default'}>
                  #{img.position + 1}{img.isPrimary && ` · ${t('roomAds.primary')}`}
                </Tag>
                <Space size={4}>
                  <Tooltip title={img.isPrimary ? t('roomAds.images.alreadyPrimary') : t('roomAds.images.markPrimary')}>
                    <Button
                      size="small"
                      type="text"
                      icon={img.isPrimary ? <StarFilled style={{ color: 'var(--warning)' }} /> : <StarOutlined />}
                      disabled={img.isPrimary || busyId !== null}
                      onClick={() => handleSetPrimary(img)}
                    />
                  </Tooltip>
                  <Tooltip title={t('common.delete')}>
                    <Button
                      size="small"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      loading={busyId === img.id}
                      onClick={() => handleDelete(img)}
                    />
                  </Tooltip>
                </Space>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && videos.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>{t('roomAds.videos.sectionTitle')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
            {videos.map((vid) => (
              <div key={vid.id} style={{
                border: '1px solid #f0f0f0', borderRadius: 8, padding: 8, background: '#fafafa', position: 'relative',
              }}>
                <video
                  src={vid.imageUrl}
                  controls
                  preload="metadata"
                  style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 4, background: '#000' }}
                />
                <div style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Tag color="purple" icon={<VideoCameraOutlined />}>#{vid.position + 1}</Tag>
                  <Tooltip title={t('common.delete')}>
                    <Button
                      size="small"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      loading={busyId === vid.id}
                      onClick={() => handleDelete(vid)}
                    />
                  </Tooltip>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
